import { FC, useMemo } from 'react';
import { faTag } from '@fortawesome/pro-regular-svg-icons';
import { BoxV2 as Box } from 'portal-commons';

import RcsSectionBlock from './RcsSectionBlock';
import InfoItem from './InfoItem';
import InfoItemLayout from './InfoItemLayout';
import { CampaignDetail, VerticalType } from '@/apis/core/types';
import { getTaxExemptStatus, getR3kStatus } from '../../utils';
import { convertTimeWithTimezone } from '@/utils/time';
import VettingScoreTooltip from '../VettingScoreTooltip';

interface BrandDetailsSectionProps {
  campaign?: CampaignDetail;
  verticalTypes?: VerticalType[];
}

const BrandDetailsSection: FC<BrandDetailsSectionProps> = ({
  campaign,
  verticalTypes = [],
}) => {
  const { brand } = campaign || {};

  const verticalType = useMemo(
    () => verticalTypes.find((item) => item.id === campaign?.brand.vertical),
    [verticalTypes, campaign?.brand.vertical]
  );

  const vettingScoreValue = () => {
    if (!Number.isInteger(campaign?.brand?.evpVettingScore)) {
      return null;
    }

    const hasFeedback =
      campaign?.brand.evpVettingFeedback &&
      campaign?.brand.evpVettingFeedback.length > 0;

    return (
      <Box
        as="div"
        sx={{ display: 'inline-flex', alignItems: 'center', height: 14 }}
        data-testid="campaignBrandDetailsVettingScore"
      >
        {campaign?.brand.evpVettingScore}
        {hasFeedback && (
          <VettingScoreTooltip data={campaign.brand.evpVettingFeedback} />
        )}
      </Box>
    );
  };
  return (
    <RcsSectionBlock icon={faTag} title="Brand Details" showDivider={true}>
      <InfoItemLayout.Vertical sx={{ mb: 'xxl' }}>
        <InfoItemLayout.Horizontal>
          <InfoItem title="Brand Legal Name">{brand?.companyName}</InfoItem>
          <InfoItem title="Brand DBA Name">{brand?.displayName}</InfoItem>
        </InfoItemLayout.Horizontal>
        <InfoItemLayout.Horizontal>
          <InfoItem title="Entity Type">{brand?.entityType}</InfoItem>
          <InfoItem title="Universal EIN">{brand?.universalEin}</InfoItem>
        </InfoItemLayout.Horizontal>
        <InfoItemLayout.Horizontal>
          <InfoItem title="Tax Number/ID">{brand?.ein}</InfoItem>
          <InfoItem title="EIN Issuing Country">
            {brand?.einIssuingCountry}
          </InfoItem>
        </InfoItemLayout.Horizontal>
        <InfoItemLayout.Horizontal>
          <InfoItem title="Brand ID">{brand?.uid}</InfoItem>
          <InfoItem title="Website/Online Presence">{brand?.website}</InfoItem>
        </InfoItemLayout.Horizontal>
        <InfoItemLayout.Horizontal>
          <InfoItem title="RUSSELL3000">{getR3kStatus(brand)}</InfoItem>
          <InfoItem title="Tax Exempt Status">
            {getTaxExemptStatus(brand)}
          </InfoItem>
        </InfoItemLayout.Horizontal>
        <InfoItemLayout.Horizontal>
          <InfoItem title="Identity Status">{brand?.identityStatus}</InfoItem>
          <InfoItem title="Vetting Score">{vettingScoreValue()}</InfoItem>
        </InfoItemLayout.Horizontal>
        <InfoItemLayout.Horizontal>
          <InfoItem title="Political Committee Locale">
            {brand?.optionalAttributes?.politicalCommitteeLocale}
          </InfoItem>
          <InfoItem title="Government Entity">
            {brand?.optionalAttributes?.governmentEntity ? 'True' : 'False'}
          </InfoItem>
        </InfoItemLayout.Horizontal>
        <InfoItemLayout.Horizontal>
          <InfoItem title="Brand Support Phone Number">{brand?.phone}</InfoItem>
          <InfoItem title="Brand Support Email Address">
            {brand?.email}
          </InfoItem>
        </InfoItemLayout.Horizontal>
        <InfoItemLayout.Horizontal>
          <InfoItem title="Brand Vertical">
            {verticalType?.displayName ?? 'N/A'}
          </InfoItem>
        </InfoItemLayout.Horizontal>
      </InfoItemLayout.Vertical>

      <RcsSectionBlock.SubSection title="Brand Address Details">
        <InfoItemLayout.Vertical sx={{ mb: 'xxl' }}>
          <InfoItemLayout.Horizontal>
            <InfoItem title="Address / Street">{brand?.street}</InfoItem>
            <InfoItem title="Postal Code">{brand?.postalCode}</InfoItem>
          </InfoItemLayout.Horizontal>
          <InfoItemLayout.Horizontal>
            <InfoItem title="City">{brand?.city}</InfoItem>
            <InfoItem title="Country">{brand?.country}</InfoItem>
          </InfoItemLayout.Horizontal>
          <InfoItemLayout.Horizontal>
            <InfoItem title="State">{brand?.state}</InfoItem>
          </InfoItemLayout.Horizontal>
        </InfoItemLayout.Vertical>
      </RcsSectionBlock.SubSection>

      <RcsSectionBlock.SubSection title="Business Contact Details">
        <InfoItemLayout.Vertical sx={{ mb: 'l' }}>
          <InfoItemLayout.Horizontal>
            <InfoItem title="Email Verified Date">
              {convertTimeWithTimezone(
                brand?.businessContactEmailVerifiedDate,
                'MM/DD/YYYY'
              )}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        </InfoItemLayout.Vertical>
      </RcsSectionBlock.SubSection>
    </RcsSectionBlock>
  );
};

export default BrandDetailsSection;
